import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AppModal } from "global/components/AppModal";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import DropzoneBody, {
  dropzoneSurfaceSx,
} from "global/components/CustomDropzone/DropzoneBody";
import { useState } from "react";
import { exportCSVFile, onAddCsv } from "./ContactFunction";
import { ContactCsvRow, pageFilters } from "models/interfaces";
import DownloadCSV_Icon from "assets/icons1/DownloadCSV_Icon.svg";
import CancelIcon from "@mui/icons-material/Cancel";

interface CustomProps {
  isUploadCsvOpen: boolean;
  setIsUploadCsvOpen: Function;
  setIsLoading: Function;
  setCount: Function;
  setHasMore: Function;
  setContacts: Function;
  contactsFilters: pageFilters;
  onlyUpload?: boolean;
  isMobile?: boolean;
}
const BulkUploadDialog = (props: CustomProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [invalidRows, setInvalidRows] = useState<ContactCsvRow[]>([]);

  const handleClose = () => {
    props.setIsUploadCsvOpen(false);
    setSelectedFile(null);
    setInvalidRows([]);
  };

  const handleDropzoneAdd = (files: File[]) => {
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      setInvalidRows([]);
    }
  };

  const handleDeleteDropZoneFile = () => {
    setSelectedFile(null);
  };

  const handleUploadContact = async () => {
    if (!selectedFile) return;
    setUploading(true);
    await onAddCsv(
      selectedFile,
      props.setIsLoading,
      props.setCount,
      props.setHasMore,
      props.setContacts,
      props.contactsFilters,
      props.setIsUploadCsvOpen,
      setInvalidRows,
    );
    setSelectedFile(null);
    setUploading(false);
  };

  const getDialogUploadCsvBody = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack
          direction={props.isMobile ? "column" : "row"}
          alignItems={props.isMobile ? "start" : "center"}
          justifyContent="space-between"
        >
          <Typography
            variant={props.isMobile ? "body2" : "body1"}
            color="text.secondary"
          >
            Choose a file to import your signers along with their details.
          </Typography>
          <Box>
            <Button
              id="download_csv_btn"
              variant="text"
              size="small"
              startIcon={
                <Stack>
                  <img src={DownloadCSV_Icon} alt="" aria-hidden="true" />
                </Stack>
              }
              onClick={() =>
                exportCSVFile(
                  [
                    "NAME",
                    "EMAIL_ID",
                    "PHONE",
                    "JOB_TITLE",
                    "COMPANY_NAME",
                    "DEPARTMENT",
                  ],
                  [
                    [
                      "John Doe",
                      "john.doe@example.com",
                      "+15551234567",
                      "Marketing Manager",
                      "Acme Corp",
                      "Marketing",
                    ],
                    [
                      "Jane Smith",
                      "jane.smith@example.com",
                      "+15559876543",
                      "Sales Director",
                      "Acme Corp",
                      "Sales",
                    ],
                  ],
                  "contacts",
                )
              }
            >
              Download example file (.csv)
            </Button>
          </Box>
        </Stack>
        <Grid item xs={12}>
          <Stack display={"flex"} flexDirection={"column"}>
            <CustomDropzone
              uniqueId="contacts-csv"
              innerBody={
                <DropzoneBody
                  title="Drop your contacts CSV here"
                  formatsText="CSV · up to 10 MB"
                />
              }
              sx={dropzoneSurfaceSx()}
              ariaLabel="Upload contacts CSV"
              onAdd={handleDropzoneAdd}
              maxFileSize={10485760}
              acceptedFiles={[".csv"]}
              showAlerts={false}
              multipleFileUpload={false}
              isUploadDocLimit={false}
            />
            {selectedFile && (
              <Chip
                label={selectedFile.name}
                variant="outlined"
                sx={{
                  mt: 1,
                  maxWidth: 200,
                  background: "transparent !Important",
                }}
                onDelete={handleDeleteDropZoneFile}
                deleteIcon={<CancelIcon />}
                size="medium"
              />
            )}
            {/* <Typography
              variant="subtitle2"
              color={"warning.main"}
              pt={1}
              sx={{
                opacity: 0.9,
              }}
            >
              * The file must be in .csv format.
            </Typography> */}
          </Stack>
        </Grid>
        {invalidRows.length > 0 && (
          <Grid item xs={12}>
            <Stack spacing={1}>
              <Typography role="status" variant="subtitle2" color="warning.main">
                {invalidRows.length} row
                {invalidRows.length === 1 ? "" : "s"} could not be imported
                (invalid email). Fix these rows and re-upload if needed —
                everything else in the file was added.
              </Typography>
              <TableContainer
                sx={{ maxHeight: 240, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Job title</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Department</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invalidRows.map((row, index) => (
                      <TableRow key={`${row.email}-${index}`}>
                        <TableCell>{row.name || "—"}</TableCell>
                        <TableCell sx={{ color: "error.main" }}>
                          {row.email || "—"}
                        </TableCell>
                        <TableCell>{row.phone || "—"}</TableCell>
                        <TableCell>{row.jobTitle || "—"}</TableCell>
                        <TableCell>{row.companyName || "—"}</TableCell>
                        <TableCell>{row.department || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Grid>
        )}
      </Stack>
    );
  };

  return (
    <AppModal
      open={props.isUploadCsvOpen}
      onClose={() => handleClose()}
      title="Upload your file"
      // An import form with a dropzone — the design system's L width.
      size="lg"
      // A staged file would be lost to a stray dismiss, and the upload itself
      // must run to completion.
      busy={uploading}
      disableBackdropClose
      disableEscapeClose
      primaryLabel={uploading ? "Uploading..." : "Upload"}
      onPrimary={handleUploadContact}
      primaryLoading={uploading}
      primaryDisabled={!selectedFile || uploading}
      // The embedded "upload only" variant offers no way out but the close
      // affordance, exactly as before.
      cancelLabel={props.onlyUpload ? undefined : "Cancel"}
      onCancel={handleClose}
    >
      {getDialogUploadCsvBody()}
    </AppModal>
  );
};

export default BulkUploadDialog;
